import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Skeleton } from "@/components/ui/skeleton";
import { TrendingUp, TrendingDown, Layers } from "lucide-react";

export default function Home() {
  const navigate = useNavigate();
  const [briefing, setBriefing] = useState(null);
  const [home, setHome] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [b, h] = await Promise.all([api.get("/home/briefing"), api.get("/home")]);
        setBriefing(b.data);
        setHome(h.data);
      } catch (e) {
        // 401 handled globally
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-32 w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="home-page">
      <div>
        <div className="text-sm text-neutral-500">{briefing?.greeting}, {briefing?.first_name}.</div>
        <h1 className="text-3xl md:text-4xl font-semibold tracking-tight mt-1" data-testid="home-heading">Today</h1>
      </div>

      <p className="text-lg md:text-xl text-neutral-800 leading-relaxed max-w-3xl" data-testid="home-briefing">
        {briefing?.paragraph}
      </p>

      {briefing?.moments?.length > 0 && (
        <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
          {briefing.moments.map((m) => (
            <button
              key={m.id}
              data-testid={`moment-${m.id}`}
              onClick={() => {
                if (m.canonical_type) navigate(`/observation/${m.canonical_type}`);
                else if (m.event_id) navigate(`/events/${m.event_id}`);
              }}
              className="text-left bg-white rounded-2xl border border-neutral-200 p-4 hover:border-neutral-300 transition-colors"
            >
              <div className="text-xs uppercase tracking-wide text-neutral-500">{m.kind}</div>
              <div className="mt-1 font-semibold text-neutral-900">{m.heading}</div>
              <div className="text-sm text-neutral-600 mt-1">{m.body}</div>
            </button>
          ))}
        </div>
      )}

      <div className="grid md:grid-cols-4 gap-3">
        <Stat label="Records" value={home?.today?.event_count ?? 0} />
        <Stat label="Biomarkers" value={home?.today?.biomarker_count ?? 0} />
        <Stat label="This week" value={home?.today?.uploads_this_week ?? 0} />
        <Stat label="Active threads" value={home?.today?.active_episodes ?? 0} />
      </div>

      {home?.changes?.length > 0 && (
        <section>
          <h2 className="text-lg font-semibold mb-3">What changed</h2>
          <div className="space-y-2">
            {home.changes.map((c, i) => (
              <div key={i} className="flex items-center justify-between bg-white rounded-xl border border-neutral-200 p-3">
                <div className="flex items-center gap-3">
                  {c.direction === "up" ? <TrendingUp className="w-4 h-4 text-emerald-600" /> : <TrendingDown className="w-4 h-4 text-rose-600" />}
                  <div className="font-medium">{c.name}</div>
                </div>
                <div className="text-sm text-neutral-600">
                  {c.from} → {c.to} {c.unit} ({c.pct > 0 ? "+" : ""}{c.pct}%)
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {home?.recent_events?.length > 0 && (
        <section>
          <h2 className="text-lg font-semibold mb-3">Recent records</h2>
          <div className="space-y-2">
            {home.recent_events.map((e) => (
              <button
                key={e.event_id}
                onClick={() => navigate(`/events/${e.event_id}`)}
                data-testid={`event-${e.event_id}`}
                className="w-full text-left bg-white rounded-xl border border-neutral-200 p-3 hover:border-neutral-300 flex items-start gap-3"
              >
                <Layers className="w-4 h-4 text-neutral-400 mt-1" />
                <div className="flex-1">
                  <div className="font-medium text-neutral-900">{e.title}</div>
                  <div className="text-xs text-neutral-500 mt-0.5">{e.date} · {e.type}</div>
                </div>
              </button>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="bg-white rounded-2xl border border-neutral-200 p-4">
      <div className="text-xs uppercase tracking-wide text-neutral-500">{label}</div>
      <div className="text-2xl font-semibold mt-1">{value}</div>
    </div>
  );
}
